/**
 * PrTrackerTab Component
 *
 * Displays baseline update pull requests created from approved drifts.
 * Part of the Baseline Compliance hub. Extracted from pages/ConfigAsCode.tsx.
 *
 * Tenant selection is handled by the parent page -- this component
 * receives the selected tenantId as a prop.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, DevOpsPullRequest } from '@/lib/api'
import toast from 'react-hot-toast'
import {
  CodeBracketIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

export interface PrTrackerTabProps {
  tenantId: string | undefined
}

const PAGE_SIZE = 10

function getStatusBadge(status: string) {
  switch (status) {
    case 'Merged':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Merged
        </span>
      )
    case 'Abandoned':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
          <XCircleIcon className="h-3.5 w-3.5" />
          Abandoned
        </span>
      )
    case 'Active':
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
          <ClockIcon className="h-3.5 w-3.5" />
          Active
        </span>
      )
  }
}

function getSeverityBadge(severity?: string) {
  const normalizedSeverity = severity?.toLowerCase()
  switch (normalizedSeverity) {
    case 'critical':
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">Critical</span>
    case 'high':
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400">High</span>
    case 'medium':
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">Medium</span>
    case 'low':
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">Low</span>
    default:
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-500/20 text-slate-400">Info</span>
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function PrTrackerTab({ tenantId }: PrTrackerTabProps) {
  const [expandedPrId, setExpandedPrId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const queryClient = useQueryClient()

  // Fetch pull requests
  const { data: prData, isLoading: prLoading, refetch } = useQuery({
    queryKey: ['devopsPullRequests', tenantId, currentPage, statusFilter],
    queryFn: () => api.getDevOpsPullRequests({
      tenantId: tenantId!,
      page: currentPage,
      pageSize: PAGE_SIZE,
      status: statusFilter || undefined,
    }),
    enabled: !!tenantId,
    staleTime: 30000,
  })

  // Fetch drifts for expanded PR
  const { data: expandedDrifts, isLoading: driftsLoading } = useQuery({
    queryKey: ['devopsPullRequestDrifts', expandedPrId, tenantId],
    queryFn: () => api.getDevOpsPullRequestDrifts(expandedPrId!, tenantId!),
    enabled: !!expandedPrId && !!tenantId,
  })

  const handleSyncAll = async () => {
    if (!tenantId) return

    try {
      toast.loading('Syncing pull request statuses...', { id: 'sync-all' })
      await api.syncAllDevOpsPullRequests(tenantId)
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['devopsPullRequests'] })
      toast.success('Pull request statuses synced successfully', { id: 'sync-all' })
    } catch (error) {
      console.error('Failed to sync PR statuses:', error)
      toast.error('Failed to sync pull request statuses', { id: 'sync-all' })
    }
  }

  const toggleExpanded = (prId: string) => {
    setExpandedPrId(expandedPrId === prId ? null : prId)
  }

  // No tenant selected state
  if (!tenantId) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden">
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 px-8 py-12 text-center border-b border-dark-700">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-500/30">
            <CodeBracketIcon className="h-12 w-12 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-4">Select a Tenant</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Please select a tenant to view their baseline update pull requests.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Filters and Actions Bar */}
      <div className="bg-dark-800 rounded-xl border border-dark-700 p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm text-slate-400">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Merged">Merged</option>
              <option value="Abandoned">Abandoned</option>
            </select>
          </div>
          <button
            onClick={handleSyncAll}
            disabled={prLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-dark-700 text-slate-300 hover:bg-dark-600 hover:text-slate-100 border border-dark-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={clsx('h-4 w-4', prLoading && 'animate-spin')} />
            Sync All
          </button>
        </div>
      </div>

      {/* Pull Requests List */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary-500/10 to-indigo-500/10 px-6 py-4 border-b border-dark-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500/20 to-indigo-500/20 rounded-lg flex items-center justify-center border border-primary-500/30">
              <CodeBracketIcon className="h-5 w-5 text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Pull Requests</h2>
              <p className="text-sm text-slate-400">
                {prData?.totalCount ?? 0} total pull request{(prData?.totalCount ?? 0) !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {prLoading ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="h-8 w-8 text-primary-400 mx-auto mb-4 animate-spin" />
            <p className="text-slate-400">Loading pull requests...</p>
          </div>
        ) : !prData?.items?.length ? (
          <div className="p-8 text-center">
            <CodeBracketIcon className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No pull requests found</p>
            <p className="text-sm text-slate-500 mt-2">
              Pull requests will appear here when drifts are approved and processed
            </p>
          </div>
        ) : (
          <div className="divide-y divide-dark-700">
            {prData.items.map((pr: DevOpsPullRequest) => (
              <div key={pr.id} className="hover:bg-dark-700/50 transition-colors">
                {/* PR Row */}
                <div
                  className="px-6 py-4 cursor-pointer"
                  onClick={() => toggleExpanded(pr.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <button className="text-slate-400 hover:text-slate-200">
                        {expandedPrId === pr.id ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono text-slate-500">#{pr.pullRequestId}</span>
                          {getStatusBadge(pr.status)}
                        </div>
                        <h3 className="text-slate-100 font-medium mt-1 truncate">{pr.title}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <UserIcon className="h-3.5 w-3.5" />
                            {pr.triggeredByDisplayName || pr.triggeredByEmail || 'System'}
                          </span>
                          <span>{formatDate(pr.createdAt)}</span>
                          <span className="text-primary-400">{pr.driftCount} drift{pr.driftCount !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded PR Details Section */}
                {expandedPrId === pr.id && (
                  <div className="px-6 pb-4 pl-16 space-y-4">
                    {/* PR Description */}
                    {pr.description && (
                      <div className="bg-dark-900/50 rounded-xl border border-dark-600 overflow-hidden">
                        <div className="px-4 py-3 border-b border-dark-600 bg-dark-700/50">
                          <h4 className="text-sm font-medium text-slate-300">Description</h4>
                        </div>
                        <div className="px-4 py-3">
                          <div className="text-sm text-slate-400 whitespace-pre-wrap prose prose-invert prose-sm max-w-none">
                            {pr.description.split('\n').map((line, i) => {
                              if (line.startsWith('## ')) {
                                return <h3 key={i} className="text-slate-200 font-semibold mt-3 mb-2">{line.slice(3)}</h3>
                              }
                              if (line.startsWith('### ')) {
                                return <h4 key={i} className="text-slate-300 font-medium mt-2 mb-1">{line.slice(4)}</h4>
                              }
                              if (line.startsWith('- ')) {
                                return <li key={i} className="ml-4 list-disc">{line.slice(2)}</li>
                              }
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return <p key={i} className="font-semibold text-slate-300">{line.slice(2, -2)}</p>
                              }
                              if (line.match(/^\*\*[^*]+:\*\*/)) {
                                const [label, ...rest] = line.split(':**')
                                return <p key={i}><span className="font-semibold text-slate-300">{label.slice(2)}:</span>{rest.join(':**')}</p>
                              }
                              if (line.trim() === '') {
                                return <br key={i} />
                              }
                              return <p key={i}>{line}</p>
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Included Drifts */}
                    <div className="bg-dark-900/50 rounded-xl border border-dark-600 overflow-hidden">
                      <div className="px-4 py-3 border-b border-dark-600 bg-dark-700/50">
                        <h4 className="text-sm font-medium text-slate-300">Included Drifts</h4>
                      </div>
                      {driftsLoading ? (
                        <div className="p-4 text-center">
                          <ArrowPathIcon className="h-5 w-5 text-primary-400 mx-auto animate-spin" />
                        </div>
                      ) : !expandedDrifts?.length ? (
                        <div className="p-4 text-center text-sm text-slate-500">
                          No drift details available
                        </div>
                      ) : (
                        <div className="divide-y divide-dark-600">
                          {expandedDrifts.map((drift) => (
                            <div key={drift.driftId} className="px-4 py-3 flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-200 font-medium">{drift.component}</span>
                                  {getSeverityBadge(drift.severity)}
                                </div>
                                <p className="text-sm text-slate-500 mt-1">{drift.setting}</p>
                              </div>
                              <div className="text-right text-sm text-slate-500">
                                {drift.approvedBy && (
                                  <div>Approved by {drift.approvedBy}</div>
                                )}
                                {drift.approvedAt && (
                                  <div>{formatDate(drift.approvedAt)}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {prData && prData.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-dark-700 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Page {prData.page} of {prData.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={!prData.hasPreviousPage}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-dark-700 text-slate-300 hover:bg-dark-600 border border-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={!prData.hasNextPage}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-dark-700 text-slate-300 hover:bg-dark-600 border border-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
